'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ALL_RESORTS_PARAM_VALUE,
  isAllResortsParam,
} from '@/utils/resort-search-params';
import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { SearchParams } from '@/constants/listing-search-keys';
import { useSearch } from '@/context/listing-search-provider';
import { NodeResort } from '@/utils/drupal-api/__generated__/graphql';
import { getListingPath } from '@/utils/paths';
import { Listing } from '@/utils/platform-api/__generated__/graphql';
import { sortListingsByName } from '@/utils/sort-listings-by-name';
import { useRouter } from 'nextjs-toploader/app';
import { ClientBrandConfig } from 'types/brand-config';

import { getListingsIndex } from '@/services/get-listings-index';
import {
  SearchFieldDropdownEmpty,
  SearchFieldDropdownItem,
  SearchFieldDropdownMenu,
  SearchFieldDropdownSearch,
} from '@/components/search/fields/search-field-dropdown-menu';
import { SearchField } from '@/components/search/fields/search-field';
import { SearchFieldPopover } from '@/components/search/fields/search-field-popover';
import { ChevronDownIcon, PinIcon } from '@/components/search/icons';
import {
  SearchFieldLayout,
  SearchFieldRadius,
  searchFieldActionIconClass,
} from '@/components/search/search-config';

export type WhereFieldProps = {
  brand: ClientBrandConfig;
  resorts?: NodeResort[];
  layout?: SearchFieldLayout;
  radius?: SearchFieldRadius;
  showDivider?: boolean;
  isLastInStack?: boolean;
  shouldSubmit?: boolean;
  submitPath?: string;
  resortId?: string | null;
  id?: string;
  className?: string;
  navigateAction?: NavigateOptions;
};

export const WhereField = ({
  brand,
  resorts,
  layout = 'stacked',
  radius = 'segment',
  showDivider = false,
  isLastInStack = false,
  shouldSubmit = false,
  submitPath,
  resortId,
  id = 'filter-where',
  className,
  navigateAction,
}: WhereFieldProps) => {
  const { showPropertyListOnSearch, showResortOnSearch } = brand;

  if (showPropertyListOnSearch) {
    return (
      <PropertyWhereField
        id={id}
        brand={brand}
        layout={layout}
        radius={radius}
        showDivider={showDivider}
        isLastInStack={isLastInStack}
        shouldSubmit={shouldSubmit}
        resortId={resortId}
        className={className}
      />
    );
  }

  if (showResortOnSearch && resorts) {
    return (
      <ResortWhereField
        id={id}
        brand={brand}
        resorts={resorts}
        layout={layout}
        radius={radius}
        showDivider={showDivider}
        isLastInStack={isLastInStack}
        shouldSubmit={shouldSubmit}
        submitPath={submitPath}
        navigateAction={navigateAction}
        className={className}
      />
    );
  }

  return null;
};

type ResortWhereFieldProps = Omit<WhereFieldProps, 'resortId'> & {
  resorts: NodeResort[];
};

const ResortWhereField = ({
  id = 'filter-where',
  brand,
  resorts,
  layout,
  radius,
  showDivider,
  isLastInStack,
  shouldSubmit,
  submitPath,
  navigateAction,
  className,
}: ResortWhereFieldProps) => {
  const { resort, setParam } = useSearch();
  const [open, setOpen] = useState(false);
  const allLabel = `All ${brand.resortPluralLabel || 'Resorts'}`;
  const options = [
    { value: '', label: allLabel },
    ...resorts.map((r) => ({
      value: r.externalId as string,
      label: r.title as string,
    })),
  ];
  const selectedResort = options.find((o) => o.value === resort);
  const displayValue = isAllResortsParam(resort)
    ? allLabel
    : resort
      ? selectedResort?.label
      : undefined;

  const handleSelect = (value: string) => {
    setParam(
      SearchParams.RESORT,
      value === '' ? ALL_RESORTS_PARAM_VALUE : value,
      shouldSubmit,
      submitPath,
      navigateAction
    );
    setOpen(false);
  };

  return (
    <SearchFieldPopover
      id={`${id}-popover`}
      className={className}
      open={open}
      onOpenChange={setOpen}
      minWidthFromTrigger
      panelTopOffset={8}
      showFooter={false}
      trigger={
        <SearchField
          id={id}
          label="WHERE?"
          value={displayValue}
          icon={<PinIcon />}
          trailing={<ChevronDownIcon className={searchFieldActionIconClass} />}
          layout={layout}
          radius={radius}
          showDivider={showDivider}
          isLastInStack={isLastInStack}
          className="w-full"
        />
      }
    >
      <SearchFieldDropdownMenu>
        {options.map((option) => {
          const isSelected =
            option.value === ''
              ? isAllResortsParam(resort)
              : option.value === resort;

          return (
            <SearchFieldDropdownItem
              key={option.value || 'all'}
              selected={isSelected}
              onSelect={() => handleSelect(option.value)}
            >
              {option.label}
            </SearchFieldDropdownItem>
          );
        })}
      </SearchFieldDropdownMenu>
    </SearchFieldPopover>
  );
};

type PropertyWhereFieldProps = Pick<
  WhereFieldProps,
  | 'id'
  | 'brand'
  | 'layout'
  | 'radius'
  | 'showDivider'
  | 'isLastInStack'
  | 'shouldSubmit'
  | 'resortId'
  | 'className'
>;

const PropertyWhereField = ({
  id = 'filter-where',
  brand,
  layout,
  radius,
  showDivider,
  isLastInStack,
  shouldSubmit,
  resortId,
  className,
}: PropertyWhereFieldProps) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Listing | null>(null);
  const [query, setQuery] = useState('');
  const [loadedListings, setLoadedListings] = useState<Listing[] | null>(null);
  const indexLoadedRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (shouldSubmit && selected) {
      router.push(getListingPath(selected, brand));
    }
  }, [shouldSubmit, selected, brand, router]);

  useEffect(() => {
    indexLoadedRef.current = false;
    setLoadedListings(null);
  }, [brand.key, resortId]);

  const loadListings = useCallback(async () => {
    if (indexLoadedRef.current) return;
    indexLoadedRef.current = true;
    try {
      const data = await getListingsIndex(brand.key, resortId ?? undefined);
      setLoadedListings(sortListingsByName(data));
    } catch {
      indexLoadedRef.current = false;
    }
  }, [brand.key, resortId]);

  useEffect(() => {
    if (!open) return;
    loadListings();
    searchInputRef.current?.focus();
  }, [open, loadListings]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery('');
    }
  };

  const handleSelect = (listing: Listing) => {
    setQuery('');
    setSelected(listing);
    setOpen(false);
  };

  const data = loadedListings ?? [];
  const filteredItems =
    query === ''
      ? data
      : data.filter((item) =>
          (item.title as string).toLowerCase().includes(query.toLowerCase())
        );
  const hasValue = selected !== null;

  return (
    <SearchFieldPopover
      id={`${id}-popover`}
      className={className}
      open={open}
      onOpenChange={handleOpenChange}
      minWidthFromTrigger
      panelTopOffset={8}
      showFooter={false}
      trigger={
        <SearchField
          id={id}
          label="WHERE?"
          value={hasValue ? (selected?.title as string) : undefined}
          placeholder="Property"
          icon={<PinIcon />}
          trailing={<ChevronDownIcon className={searchFieldActionIconClass} />}
          layout={layout}
          radius={radius}
          showDivider={showDivider}
          isLastInStack={isLastInStack}
          className="w-full"
        />
      }
    >
      <SearchFieldDropdownSearch
        inputRef={searchInputRef}
        value={query}
        onChange={setQuery}
        placeholder="Search properties"
        aria-label="Search properties"
      />
      {filteredItems.length > 0 ? (
        <SearchFieldDropdownMenu className="py-0">
          {filteredItems.map((item) => (
            <SearchFieldDropdownItem
              key={item.id}
              selected={selected?.id === item.id}
              onSelect={() => handleSelect(item)}
            >
              {item.title}
            </SearchFieldDropdownItem>
          ))}
        </SearchFieldDropdownMenu>
      ) : (
        <SearchFieldDropdownEmpty>
          {loadedListings === null ? 'Loading properties…' : 'No properties found'}
        </SearchFieldDropdownEmpty>
      )}
    </SearchFieldPopover>
  );
};
